<style lang="scss" scoped>
.upload {
	&__pad-bottom {
		margin-bottom: 30px;
	}
	&__text {
		padding: 20px;
		text-align: center;
	}

	&__header {
		@include flex-center;
		&__buttons {
			margin-left: auto;
			> button, div {
				margin-left: 15px;
			}
		}
	}
}

.padding {
	padding: 20px 40px;
}
.progress-bar {
	margin: 40px 0px 0px 0px;
}
.content {
	margin-top: 20px;
}
</style>

<template lang="pug">
.upload
	app-header
		.upload__header(slot="header")
			h1.upload__header__title {{ providerName }} School Manager Sync
			.upload__header__buttons
				el-button(type="success", @click="download", icon='fe fe-download fe-just-off', :loading="csvLoading || !updatedCache").ml-auto Download
				el-button(type="primary", @click="upload", icon='fe fe-repeat fe-just-off', :loading="loading && !workCompleted" v-if="proceedSync || synced").ml-auto Sync
	.container
		.main-panel
			div.padding(v-if="!loading")
				p(v-if="synced")
					| Wonde will perform sync to {{ providerFullName }}. You may download your school data or check the preview below and review it to ensure everything is in place. Click <b>Sync</b> to proceed.
				p(v-else)
					| As this is your first time running a sync to {{ providerFullName }} via Edusync Apple, you will need to download your school data by clicking the <b>Download</b> button and 
					| update the Person IDs of your Apple users to the IDs provided in the <b>person_id</b> column to avoid duplication of users.
					| <br><br>If you have updated the Person IDs of your users or if you have not yet setup your {{ providerFullName }} environment, please check the checkbox below then click <b>Sync</b> to proceed.
				p(v-if="!synced")
					el-form(label-position='top')
						el-form-item
							el-checkbox(v-model="proceedSync") Proceed with Sync
				 
			div(v-if="loading && !workCompleted")
				loading(:message="progress && progress.meta.progress ? [progress.meta.progress] : loadingMessage")
					el-progress.progress-bar(:percentage="progress && progress.meta.percent ? progress.meta.percent : 0", :text-inside="true", :stroke-width="18", text-color="#fff")
			empty(:message="emptyMessage", v-if="workCompleted && !failedUploadMessage")
				div.content(slot="content" v-if="!school.Organisation || !schoolSettings.org_sync_enabled")
					el-button(type="primary" @click="openASM") {{ providerName }} School Manager
			empty(:message="failedUploadMessage", v-if="workCompleted && failedUploadMessage")

			div.padding
				el-input(placeholder="Search CSV data" v-model="term" prefix-icon="el-icon-search", v-if="currentTab !== 'locations.csv'")
			el-tabs(type="card", @tab-click="handleTabClick", value="locations.csv")
				el-tab-pane(v-for="(item, i) in csvFileNames" :key="i", :label="item", :name="item")
			csv-grid(:data="csvData", :isLoading="csvLoading")
</template>

<script>
import gql from "graphql-tag";
import Bugsnag from '@bugsnag/js';
import csvGrid from "./includes/CSVGrid";
import JSZip, { filter } from "jszip";
import csvToJSON from "csvtojson"
import Fuse from "fuse.js";
import _ from "lodash";
const { Parser } = require('json2csv');

export default {
	components: {
		csvGrid
	},
	data() {
		return {
			version: "v1",
			loading: false,
			loadingMessage: [`Fetching data...`],
			emptyMessage: "",
			uuid: "",
			work: null,
			requestVariables: {
				version: "v1",
				csvs: ["locations","staff","students", "classes","courses","rosters"],
				format: "standard"
			},
			csvLoading: false,
			updatedCache: true,
			proceedSync: false,
			school: {
				Organisation: null
			},
			csvData: [],
			csvFileNames: [],
			zipData: null,
			tabLoaded: false,
			term: null,
			currentTab: null,
			students: [],
			staff: []
		};
	},
	mounted() {
		this.cacheClear();
		this.csvLoading = true;
	},
	apollo: {
		work: {
			query: gql`
				query GetWorks($where: WorkWhereInput, $order: WorkOrderByInput) {
					Work(where: $where, order: $order) {
						uuid
						payload
					}
				}
			`,
			variables() {
				return {
					where: {
						uuid: this.uuid,
						type: "school"
					}
				};
			},
			update: r => _.cloneDeep(r.Work),
			pollInterval: 3000,
			fetchPolicy: "no-cache",
			skip() {
				return this.workCompleted || !this.uuid;
			},
		},
		csv: {
			query: gql`
				query DownloadSchoolData($version: String!, $csvs: [String!]!, $format: SchoolDataFormatEnum!) {
					DownloadSchoolData(version: $version, csvs: $csvs, format: $format) {
						content
					}
				}
			`,
			variables() {
				return {
					...this.requestVariables,
					csvs: ["staff_full", "students_full", "classes_full", "courses_full", "rosters_full"].concat(this.requestVariables.csvs)
				}
			},
			update(r) {
				if (r.DownloadSchoolData && !_.isNil(r.DownloadSchoolData.content)) {
					this.csvLoading = false;
				}

				return _.cloneDeep(r.DownloadSchoolData)
			},
			pollInterval: 5000,
			fetchPolicy: "no-cache",
			skip() {
				return !this.csvLoading || !this.updatedCache;
			},
		},
		synced: {
			query: gql`
				query Activities($schema: String!) {
					Activities(schema: $schema) {
						name
					}
				}
			`,
			variables() {
				return {
					schema: "sync_finish"
				}
			},
			update(r) {
				return r.Activities.length > 0
			},
			fetchPolicy: "no-cache",
		},
		school: {
			query: gql`
				{
					School {
						Organisation {
							id
							name
						}
					}
				}
			`,
			update: (r) => r.School,
		},
		schoolSettings: {
			query: gql`
				{
					Settings {
						org_sync_enabled
						id
						student_id_field
						employee_id_field
					}
				}
			`,
			 update(r) {
                return _.cloneDeep(r.Settings);
            },
		},
		users: {
			query: gql`
				query Directory {
					Directory {
						WondeUsers {
							id
							mis_id
							mis_secondary_id
							email
							base_type
						}
					}
				}
			`,
			update(r) {
				this.students = _(r.Directory.WondeUsers)
					.filter((user) => {
						return user.base_type === "student";
					})
					.keyBy((user) => {
						const personId = this.schoolSettings[`${user.base_type}_id_field`] === "edusync_id" ? "id" : this.schoolSettings[`${user.base_type}_id_field`];
						return user[personId];
					})
					.value();
				
				this.staff = _(r.Directory.WondeUsers)
					.filter((user) => {
						return user.base_type === "employee";
					})
					.keyBy((user) => {
						const personId = this.schoolSettings[`${user.base_type}_id_field`] === "edusync_id" ? "id" : this.schoolSettings[`${user.base_type}_id_field`];
						return user[personId];
					})
					.value();
			},
			fetchPolicy: "no-cache",
		},
	},
	computed: {
		progress() {
			if (this.work && this.work.length) {
				try {
					return JSON.parse(this.work[0].payload)
				} catch (e) {
					Bugsnag.notify(e);
					return null;
				}
			} 
			return null;
		},
		workCompleted() {
			return !_.isNil(this.progress) && this.progress.meta.percent === 100;
		},
		failedUploadMessage() {
			return this.workCompleted && this.progress.meta.progress.includes("error") ? this.progress.meta.progress : "";
		},
	},
	methods: {
		async upload() {
			this.work = null;
			this.loading = true;

			try {
				const uploaded = await this.$apollo
					.mutate({
						mutation: gql`
							mutation UploadSchoolDataMutation($version: String!, $csvs: [String!]!, $format: SchoolDataFormatEnum!) {
								UploadSchoolDataMutation(version: $version, csvs: $csvs, format: $format)
							}
						`,
						variables: this.requestVariables
					});
				this.uuid = uploaded.data.UploadSchoolDataMutation;
				this.emptyMessage = this.school.Organisation && this.schoolSettings.org_sync_enabled ? `Uploading MIS/SIS data is complete. Your data will be uploaded to ${this.providerName} School Manager once your organisation runs sync` : `Uploading MIS/SIS data is complete but sync process may take a while. Open ${this.providerName} School Manager to check status.`;
			} catch (e) {
				Bugsnag.notify(e);
				this.emptyMessage = "Failed to upload MIS/SIS data, please contact support if issue continues.";
			}
		},
		async download() {
			if(this.csv && !_.isNil(this.csv.content)) {
				const zip = await this.getZipData();
				
				zip.forEach(async (relativePath, file) => {
					if (relativePath.includes("full")) {
						zip.remove(relativePath);
					}
				})

				const content = await zip.generateAsync({ type: "base64" });
				const linkSource = `data:application/zip;base64,${content}`;
				let downloadLink = document.createElement("a");
				downloadLink.href = linkSource;
				downloadLink.download = `AppleSchoolManager.zip`;
				downloadLink.click();
			}
		},
		async cacheClear() {
			this.updatedCache = false;
			await this.$apollo.mutate({
				mutation: gql`
					mutation CacheClear($item: CacheTypeEnum!) {
						CacheClear(item: $item)
					}`,
					variables: {
						item: "csv"
					}
			});
			this.updatedCache = true;
		},
		openASM() {
			window.open("https://school.apple.com", "_blank");
		},
		async getZipData() {
			if (this.csv && !_.isNil(this.csv.content))  {
				const zip = new JSZip();
				
				return await zip.loadAsync(this.csv.content, { base64: true });	
			}
		},
		async loadCSV(fileName) {
			const zip = await this.getZipData();
			const data = await zip.file(fileName).async("string");

			this.csvData = await csvToJSON().fromString(data);
			await this.transformData(zip, fileName);
			this.tabLoaded = true;
		},
		async transformData(zip, fileName) {
			const type = fileName.replace(".csv", "");
			let data;
			let additionalData;
			try {
				data = await zip.file(`${type}_full.csv`).async("string");
				if(data) {
					additionalData = await csvToJSON().fromString(data);
				}
			} catch (e) {
				Bugsnag.notify(e);
				this.emptyMessage = `Failed to load ${type}_full.csv.`;
			}

			let filteredData;
			
			if (additionalData) {
				const firstData = _.first(this.csvData);
				switch(fileName) {
					case "classes.csv":
						additionalData = _.map(additionalData, (cls) => {
							cls.filtered = true;
							return cls;
						});
						filteredData = _.differenceWith(additionalData, this.csvData, (unfilteredClass, idpClass) => unfilteredClass.class_id == idpClass.class_id);
						this.csvData = [...this.csvData, ...filteredData];
						break;
					case "courses.csv":
						additionalData = _.map(additionalData, (cls) => {
							cls.filtered = true;
							return cls;
						});
						filteredData = _.differenceWith(additionalData, this.csvData, (unfilteredCourse, idpCourse) => unfilteredCourse.course_id == idpCourse.course_id);
						this.csvData = [...this.csvData, ...filteredData];
						break;
					case "rosters.csv":
						additionalData = _.map(additionalData, (cls) => {
							cls.filtered = true;
							return cls;
						});
						filteredData = _.differenceWith(additionalData, this.csvData, (unfilteredRoster, roster) => unfilteredRoster.course_id == roster.course_id);
						this.csvData = [...this.csvData, ...filteredData];
						break;
					case "staff.csv":
					case "students.csv":
						this.csvData = _.map(this.csvData, (user) => {
							user.email_address_orig = this[type][user.person_id] ? this[type][user.person_id].email : null;
							return user;
						});
						additionalData = _.map(additionalData, (cls) => {
							cls.filtered = true;
							return cls;
						});
						filteredData = _.differenceWith(additionalData, this.csvData, (unfilteredUser, idpUser) => unfilteredUser.person_id == idpUser.person_id);
						this.csvData = [...this.csvData, ...filteredData];
						break;
				}
			}
		},
		searchCSV: _.debounce(function(self, newVal) {
			if (newVal) {
				let options = {};

				switch (this.currentTab) {
					case "classes.csv":
						options = {
							keys: ["class_id", "class_number", "course_id"],
						}
					break;
					case "courses.csv": 
						options = {
							keys: ["subject_id", "subject_code", "subject"],
						}
					break;
					case "rosters.csv":
						options = {
							keys: ["id", "class_id", "person_id"],
						}
					break;
					default:
						options = {
							keys: ["first_name", "last_name", "email_address"],
						};
					break;	
				}

				const fuse = new Fuse(this.csvData, options);

				this.csvData = fuse.search(newVal);
			} else {
				this.loadCSV(this.currentTab);
			}
		}, 300),
		async getCSVFileNames() {
			const zip = await this.getZipData();

			if (zip) {
				this.csvFileNames = _.filter(Object.keys(zip.files), (fileName) => {
					return !fileName.includes("_full");
				});

				if (!this.tabLoaded) {
					this.loadCSV(this.csvFileNames[0]);
				}
			}
			
		},
		handleTabClick(tab, event) {
			this.currentTab = tab.paneName;
			this.loadCSV(tab.paneName)
		}
	},
	watch: {
		csv() {
			this.getCSVFileNames();
		},
		term(newVal) {
			this.searchCSV(this, newVal)
		}
	}
};
</script>
